import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import * as Sentry from '@sentry/react';
import { Channel, MovieDetails, NowAndNextApiResponse, ProgramDetails, ContinueWatching } from 'types/Asset';
import { unixTimeToMultiplesOf } from 'utils/fnDate';
import { isSafari } from 'utils/fnDevices';
import { Collection, navigationParser, pageParser } from '../utils/fnParser';
import { ActiveSessionsResponse, PlaybackPositionsResponse } from '../types/Player';
import { ApiSource, FetchResponse, StreamFormat, StreamIDKey, ServiceQueryParams, DRMType } from '../types/ApiTypes';
import { CustomerDetailsResponse } from '../types/CustomerDetails';
import localConfig from '../config/localConfig';
import { appendQueryParams } from '../utils/fnUrl';
import { captureException } from '../utils/fnError';
import {
    executeWithUserToken,
    getAccountCacheKey,
    isGuestUser,
    tokenRequestHeader,
    waitForAccountSession,
} from '../providers/useAuth/AuthService';
import { DEFAULT_ATTEMPTS, EXTRA_ATTEMPTS, SINGLE_ATTEMPT } from '../utils/fnBackoff';
import {
    accountPath,
    channelsPath,
    collectionPath,
    navigationPath,
    pagePath,
    vodPath,
    vodFavoritePath,
    vodSeriesPath,
    favoritesPath,
    myFavoritesPath,
    guidePath,
    channelGroups,
    listingPath,
    listingFavoritePath,
    listingPlaybackPositionsPath,
    listingProgramFavoritePath,
    streamSessionPath,
    streamSessionWithIdPath,
    programsPath,
    programSeriesFavoritePath,
    validatePinPath,
    nowAndNextPath,
    continueWatchingPath,
    purchasePath,
    playbackPositionsPath,
    VODPlaybackPositionsPath,
    programSeriesPath,
    programSeriesEpisodesPath,
    programSeriesNextEpisodePath,
    VODSeriesPath,
    VODSeriesEpisodesPath,
    VODSeriesNextEpisodePath,
    changePinPath,
    recordingsPath,
    watchlistPath,
    recordingByIdPath,
    servicesPath,
    servicePurchasePath,
    searchPath,
    customerDetailsPath,
} from './apiPaths';
import { PageData } from '../types/Page';
import { CollectionParams } from '../types/Collection';
import { Navigation, NavigationAllLanguages } from '../types/Navigation';
import { AccountInfo } from '../types/Account';
import { getLocalStorage } from '../utils/fnStorage';
import DRM from '../providers/player/DRM';

const getOS = () => {
    let os = 'Unknown OS';

    if (navigator.userAgent.indexOf('Win') !== -1) os = 'Windows';
    if (navigator.userAgent.indexOf('Mac') !== -1) os = 'Mac OS';
    if (navigator.userAgent.indexOf('X11') !== -1) os = 'UNIX';
    if (navigator.userAgent.indexOf('Linux') !== -1) os = 'Linux';
    if (navigator.userAgent.indexOf('iPhone') !== -1) os = 'iPhone';
    if (navigator.userAgent.indexOf('iPad') !== -1) os = 'iPad';
    if (navigator.userAgent.indexOf('iPod') !== -1) os = 'iPod';
    if (navigator.userAgent.indexOf('Android') !== -1) os = 'Android';

    return os;
};

let lastChannelsRequestCacheKey: string | null = null;

export const getLastChannelsRequestCacheKey = (): string | null => lastChannelsRequestCacheKey;

export const apiRequest = async <T>({ url, options, dataParser }: ApiSource<T>): Promise<FetchResponse<T>> => {
    let requestStatus: number;

    try {
        let json: T;
        const response = await Promise.race([
            axios(url, options),
            new Promise<AxiosResponse<T>>((resolve, reject) => {
                if (!navigator.onLine) {
                    reject(new Error(localConfig.data.networkError));
                }
            }),
        ]);

        requestStatus = response.status;

        if (requestStatus >= 400) {
            return {
                error: response,
                status: requestStatus,
                response: null,
            };
        }

        json = response.data;

        if (dataParser) {
            json = dataParser(json);
        }

        return {
            error: null,
            status: requestStatus,
            response: json,
        };
    } catch (exception) {
        requestStatus = exception?.response?.status;

        if (captureException(exception)) {
            Sentry.captureException(exception);
        }

        return {
            error: exception,
            status: requestStatus,
            response: null,
        };
    }
};

const apiExecute = async <T>(
    url: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    headers: { [key: string]: string },
    query: { [key: string]: boolean | number | string },
    body: unknown,
    dataParser: { (data: any): any },
    skipHeaders = false,
    maxAttempts = DEFAULT_ATTEMPTS
): Promise<FetchResponse<T>> => {
    const { skipPageParam, ...cleanQuery } = query as ServiceQueryParams;
    const requestQueries = method === 'GET' && !skipPageParam ? { ...{ page: 1 }, ...cleanQuery } : cleanQuery;

    // Lazy evaluation function for headers - called AFTER token refresh
    const buildHeaders = () => {
        const appLanguage = getLocalStorage('appLanguage') || 'et';

        if (skipHeaders) {
            return {
                ...tokenRequestHeader(false),
                ...headers,
            };
        }

        const requestHeaders = {
            ...tokenRequestHeader(),
            ...{
                'Content-Type': 'application/json',
                'GSTR-App-Language': appLanguage,
                'GSTR-App-Device-ID': DRM.getDeviceId(),
                'GSTR-App-Device-Name': getOS(),
                'GSTR-App-Code': 'browser',
            },
            ...headers,
        };

        Object.keys(requestHeaders).forEach((key) => {
            if (requestHeaders[key] === undefined) {
                delete requestHeaders[key];
            }
        });

        return requestHeaders;
    };

    const doFetch = () => {
        const requestHeaders = buildHeaders();

        const options: AxiosRequestConfig = {
            method,
            headers: requestHeaders,
            data: body,
            timeout: 43000,
            timeoutErrorMessage: localConfig.data.networkError,
        };

        if (!skipHeaders && method === 'GET') {
            options.headers['Cache-Control'] = 'no-cache';
            options.headers.Pragma = 'no-cache';
        }

        return apiRequest<T>({
            url: appendQueryParams(url, requestQueries as { [key: string]: string }),
            options,
            dataParser,
        });
    };

    return executeWithUserToken<T>(doFetch, maxAttempts);
};

/** Green Streams API */
export default class GSApi {
    /** User specific requests */

    static readonly fetchAccountInfo = async (): Promise<FetchResponse<AccountInfo>> =>
        executeWithUserToken<AccountInfo>(() => {
            const headers = tokenRequestHeader();
            delete headers['GSTR-Account-Cache-Key'];
            headers['Cache-Control'] = 'no-cache';
            headers.Pragma = 'no-cache';

            return apiRequest<AccountInfo>({
                url: accountPath(),
                options: {
                    method: 'GET',
                    headers,
                },
            });
        }, EXTRA_ATTEMPTS);

    /** NAVIGATION */
    static readonly fetchNavigation = async (language: string): Promise<FetchResponse<Navigation>> =>
        apiExecute(
            navigationPath(),
            'GET',
            { 'GSTR-App-Language': language },
            {},
            null,
            (data) => navigationParser(data, language),
            false,
            EXTRA_ATTEMPTS
        );

    static readonly fetchNavigationForAllLanguages = async (): Promise<FetchResponse<NavigationAllLanguages>> => {
        const languages = ['en', 'et', 'ru'];
        const promises = [];

        languages.forEach((language) => {
            promises.push(GSApi.fetchNavigation(language));
        });

        return Promise.all(promises).then((responses) => {
            const errorResponse = responses.find((response) => response.error);
            if (errorResponse) {
                return {
                    response: null,
                    error: errorResponse.error,
                    status: errorResponse.status,
                };
            }

            return {
                response: {
                    en: responses[0]?.response,
                    et: responses[1]?.response,
                    ru: responses[2]?.response,
                },
                error: null,
                status: 200,
            };
        });
    };

    /** PAGES */
    static readonly fetchPage = async (pageId: string): Promise<FetchResponse<PageData>> => {
        const appLanguage = getLocalStorage('appLanguage') || 'et';
        return apiExecute(pagePath(pageId), 'GET', {}, {}, null, (data) => pageParser(data, appLanguage));
    };

    /** CHANNELS */
    static readonly fetchChannels = async <T>(
        parser: (data: any) => Collection<Channel>,
        skipCache: boolean = false
    ): Promise<FetchResponse<T>> => {
        const cacheKeyReady = await waitForAccountSession();
        lastChannelsRequestCacheKey = cacheKeyReady ? getAccountCacheKey() : null;

        const state = isGuestUser() ? 'guest' : 'logged';
        const invalidateCache = skipCache ? `&_=${Date.now()}` : '';
        return apiExecute(
            `${channelsPath()}?state=${state}${invalidateCache}`,
            'GET',
            cacheKeyReady ? {} : { 'GSTR-Account-Cache-Key': undefined },
            {},
            null,
            parser
        );
    };

    /** CHANNEL GROUPS */
    static readonly fetchChannelGroups = async <T>(parser?: (data: any) => T): Promise<FetchResponse<T>> =>
        apiExecute(channelGroups(), 'GET', {}, {}, null, parser);

    static readonly fetchGuide = async <T>(
        channelIds: string[],
        startTime: number,
        endTime: number,
        page: number,
        parser: (data: any) => any
    ): Promise<FetchResponse<T>> => {
        const start = unixTimeToMultiplesOf(startTime, 3600);
        const end = unixTimeToMultiplesOf(endTime, 3600);
        const duration = end - start;

        return apiExecute(
            guidePath(),
            'GET',
            {},
            {
                duration,
                start,
                page,
                ...(channelIds.length > 0
                    ? { channel_ids: channelIds.map((id: string, i: number) => (i > 0 ? `&channel_ids=${id}` : id)).join('') }
                    : {}),
            },
            null,
            parser
        );
    };

    static readonly fetchFavorites = async <T>(parser: (data: any) => any): Promise<FetchResponse<T>> => {
        const cacheKeyReady = await waitForAccountSession();
        return apiExecute(favoritesPath(), 'GET', cacheKeyReady ? {} : { 'GSTR-Account-Cache-Key': undefined }, {}, null, parser);
    };

    static readonly updateFavorites = async <T>(body: { channel_ids: string[] }, parser: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(favoritesPath(), 'PUT', {}, {}, body, parser);

    static readonly fetchListing = async <T>(
        programId: string,
        dataParser?: (data: any) => any,
        skipCache: boolean = false
    ): Promise<FetchResponse<T>> => {
        const params: Record<string, string> = {
            image_size: 'medium', // not sure why this is required to fetch the listing.
        };

        // Safari ignores Cache-Control request headers and serves old responses from disk cache.
        // A unique query param breaks the cache :) Scoped to Safari so other browsers keep edge caching.
        if (skipCache && isSafari()) {
            params._ = Date.now().toString();
        }

        return apiExecute<T>(listingPath(programId), 'GET', {}, params, null, dataParser);
    };

    /** COLLECTIONS */
    static readonly getCollection = async <T>(
        collectionId: string,
        params?: CollectionParams,
        dataParser?: (data: any) => any
    ): Promise<FetchResponse<T>> => {
        const collectionPrams: CollectionParams = {
            page: 1,
            per_page: 20,
            image_size: 'medium',
            ...params,
        };

        return apiExecute<T>(collectionPath(collectionId), 'GET', {}, collectionPrams, null, dataParser);
    };

    /** MOVIE DETAILS */
    static readonly fetchVodMovieDetails = async <T>(id: string, parser: (data: any) => MovieDetails): Promise<FetchResponse<T>> =>
        apiExecute(vodPath(id), 'GET', {}, { image_aspect_ratio: '2:3' }, null, parser);

    /** VOD EPISODE DETAILS */
    static readonly fetchVodEpisodeDetails = async <T>(id: string, parser: (data: any) => MovieDetails): Promise<FetchResponse<T>> =>
        apiExecute(vodPath(id), 'GET', {}, { image_aspect_ratio: '16:9' }, null, parser);

    /** PROGRAM DETAILS */
    static readonly fetchProgramDetails = async <T>(
        programSeriesId: string,
        parser: (data: any) => ProgramDetails
    ): Promise<FetchResponse<T>> => apiExecute(programsPath(programSeriesId), 'GET', {}, {}, null, parser);

    /** PROGRAM SERIES DETAILS */
    static readonly fetchProgramSeriesDetails = async <T>(programSeriesId: string, parser: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(programSeriesPath(programSeriesId), 'GET', {}, {}, null, parser);

    static readonly fetchProgramSeriesEpisodes = async <T>(
        programSeriesId: string,
        parser: (data: any) => ProgramDetails
    ): Promise<FetchResponse<T>> => apiExecute(programSeriesEpisodesPath(programSeriesId), 'GET', {}, {}, null, parser);

    static fetchProgramSeriesNextEpisode = async <T>(programSeriesId: string, parser: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(programSeriesNextEpisodePath(programSeriesId), 'GET', {}, {}, null, parser);

    /** VOD Series */
    static readonly fetchVodSeriesDetails = async <T>(vodSeriesId: string, parser: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(VODSeriesPath(vodSeriesId), 'GET', {}, {}, null, parser);

    static readonly fetchVodSeriesEpisodes = async <T>(
        vodSeriesId: string,
        parser: (data: any) => ProgramDetails
    ): Promise<FetchResponse<T>> => apiExecute(VODSeriesEpisodesPath(vodSeriesId), 'GET', {}, {}, null, parser);

    static readonly fetchVodSeriesNextEpisode = async <T>(vodSeriesId: string, parser: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(VODSeriesNextEpisodePath(vodSeriesId), 'GET', {}, {}, null, parser);

    /** STREAM SESSIONS */
    static readonly createStreamSession = async (
        useCase: string,
        assetKey: StreamIDKey,
        assetId: string,
        body?: any,
        parser?: (data: any) => any
    ): Promise<FetchResponse<unknown>> => {
        // !any !unknown
        const bodyObj = {
            ...{
                use_case: useCase,
                drm: isSafari() ? DRMType.FAIRPLAY : DRMType.WIDEVINE,
                stream_format: isSafari() ? StreamFormat.HLS : StreamFormat.DASH,
                adaption_profile: 'WEBTV', // config-cc says: tv = WEBTV, vod = WEB
                [assetKey]: assetId,
            },
            ...body,
        };

        return apiExecute(streamSessionPath(), 'POST', {}, {}, bodyObj, parser);
    };

    static readonly activeStreamSessions = async (parser?: (data: any) => any): Promise<FetchResponse<ActiveSessionsResponse>> =>
        // !any !unknown
        apiExecute(streamSessionPath(), 'GET', {}, {}, null, parser);

    static readonly heartbeatStreamSession = async (sessionId: string, parser?: (data: any) => any): Promise<FetchResponse<unknown>> =>
        // !any !unknown
        apiExecute(streamSessionWithIdPath(sessionId), 'PUT', {}, {}, null, parser, false, EXTRA_ATTEMPTS);

    static readonly endStreamSession = async (sessionId: string, parser?: (data: any) => any): Promise<FetchResponse<unknown>> =>
        // !any !unknown
        apiExecute(streamSessionWithIdPath(sessionId), 'DELETE', {}, {}, null, parser);

    static readonly validatePin = async <T>(pin: string, parser?: (data: any) => any): Promise<FetchResponse<T>> => {
        const bodyObj = {
            pin,
        };

        const response = await apiExecute(validatePinPath(), 'POST', {}, {}, bodyObj, parser, false, SINGLE_ATTEMPT);

        return { response: (response.status === (204 as unknown)) as unknown as T, status: response.status, error: null };
    };

    static readonly changePin = async <T>(old_pin: string, new_pin: string, parser?: (data: any) => any): Promise<FetchResponse<T>> => {
        const bodyObj = {
            old_pin,
            new_pin,
        };

        const response = await apiExecute(changePinPath(), 'PUT', {}, {}, bodyObj, parser);

        return { response: (response.status === (204 as unknown)) as unknown as T, status: response.status, error: null };
    };

    /** PURCHASE / RENT  */

    static readonly purchaseVod = async <T>(
        vodTermId: string,
        purchasePin: string,
        parser?: (data: any) => any
    ): Promise<FetchResponse<T>> => {
        const bodyObj = {
            vod_term_id: vodTermId,
            purchase_pin: purchasePin,
        };

        return apiExecute(purchasePath(), 'POST', {}, {}, bodyObj, parser);
    };

    static readonly getVodPurchases = async <T>(
        imageSize: string,
        page: number,
        image_aspect_ratio: string,
        parser?: (data: any) => any
    ): Promise<FetchResponse<T>> => {
        const params = {
            image_size: imageSize,
            page,
            image_aspect_ratio,
        };

        return apiExecute(purchasePath(), 'GET', {}, params, {}, parser);
    };

    /** Play Session */

    static readonly getPlaybackPositions = async (parser?: (data: any) => any): Promise<FetchResponse<PlaybackPositionsResponse>> =>
        // !any !unknown
        apiExecute(playbackPositionsPath(), 'GET', {}, {}, null, parser);

    static readonly createVODPlaybackPosition = async (
        vodId: string,
        position: number,
        parser?: (data: any) => any
    ): Promise<FetchResponse<PlaybackPositionsResponse>> => {
        const bodyObj = {
            position,
        };
        return apiExecute(VODPlaybackPositionsPath(vodId), 'POST', {}, {}, bodyObj, parser);
    };

    static readonly removeVODPlaybackPosition = async (
        vodId: string,
        parser?: (data: any) => any
    ): Promise<FetchResponse<PlaybackPositionsResponse>> => apiExecute(VODPlaybackPositionsPath(vodId), 'DELETE', {}, {}, null, parser);

    // Save playback pos for unload paths; keepalive lets it outlive the document (even when closing it suddenly).
    static readonly sendPlaybackPositionKeepalive = (id: string, position: number, isVod: boolean): void => {
        try {
            fetch(isVod ? VODPlaybackPositionsPath(id) : listingPlaybackPositionsPath(id), {
                method: 'POST',
                headers: tokenRequestHeader(),
                body: JSON.stringify({ position }),
                keepalive: true,
            }).catch(() => {});
        } catch {
            // Never break page teardown.
        }
    };

    static readonly createListingPlaybackPosition = async (
        listingId: string,
        position: number,
        parser?: (data: any) => any
    ): Promise<FetchResponse<PlaybackPositionsResponse>> => {
        const bodyObj = {
            position,
        };
        return apiExecute(listingPlaybackPositionsPath(listingId), 'POST', {}, {}, bodyObj, parser);
    };

    /**  Favorites */

    static readonly getWatchlist = async <T>(
        page: number,
        per_page: number,
        image_size: string,
        watchlist_type: string,
        image_aspect_ratio: string,
        parser?: (data: any) => any
    ): Promise<FetchResponse<T>> => {
        const params = {
            page,
            per_page,
            image_size,
            watchlist_type,
            image_aspect_ratio,
        };

        return apiExecute(watchlistPath(), 'GET', {}, params, {}, parser);
    };

    static readonly getMyFavorites = async <T>(parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(myFavoritesPath(), 'GET', {}, {}, null, parser);

    /** VOD Favorites */

    static readonly createVodFavorite = async <T>(vodId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(vodFavoritePath(vodId), 'POST', {}, {}, null, parser);

    static readonly removeVodFavorite = async <T>(vodId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(vodFavoritePath(vodId), 'DELETE', {}, {}, null, parser);

    /** Program-series Favorites */

    static readonly createProgramSeriesFavorite = async <T>(programId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(programSeriesFavoritePath(programId), 'POST', {}, {}, null, parser);

    static readonly removeProgramSeriesFavorite = async <T>(programId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(programSeriesFavoritePath(programId), 'DELETE', {}, {}, null, parser);

    /** VOD Series Favorites */

    static readonly createVodSeriesFavorite = async <T>(vodId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(vodSeriesPath(vodId), 'POST', {}, {}, null, parser);

    static readonly removeVodSeriesFavorite = async <T>(vodId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(vodSeriesPath(vodId), 'DELETE', {}, {}, null, parser);

    /** Listing program Favorites */

    static createListingProgramFavorite = async <T>(listingId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(listingProgramFavoritePath(listingId), 'POST', {}, {}, null, parser);

    static removeListingProgramFavorite = async <T>(listingId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(listingProgramFavoritePath(listingId), 'DELETE', {}, {}, null, parser);

    /** Listing Favorites */

    static createListingFavorite = async <T>(listingId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(listingFavoritePath(listingId), 'POST', {}, {}, null, parser);

    static removeListingFavorite = async <T>(listingId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(listingFavoritePath(listingId), 'DELETE', {}, {}, null, parser);

    /** Now and Next */

    static readonly getNowAndNext = async (parser?: (data: any) => any): Promise<FetchResponse<NowAndNextApiResponse>> =>
        apiExecute(nowAndNextPath(), 'GET', {}, {}, null, parser);

    /** Continue watching */

    static readonly continueWatching = async (
        parser?: (data: any) => any,
        perPage = 20,
        page = 1
    ): Promise<FetchResponse<ContinueWatching>> => apiExecute(continueWatchingPath(), 'GET', {}, { page, per_page: perPage }, null, parser);

    /** Recordings */

    static readonly getRecordings = async <T>(parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(recordingsPath(), 'GET', {}, {}, null, parser);

    static createRecordings = async <T>(listingId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> => {
        const body = {
            listing_id: listingId,
        };
        return apiExecute(recordingsPath(), 'POST', null, {}, body, parser);
    };

    static readonly deleteAllRecordings = async <T>(parser?: (data: any) => any): Promise<FetchResponse<T>> => {
        const body = {
            all: true,
        };
        return apiExecute(recordingsPath(), 'DELETE', null, {}, body, parser);
    };

    static readonly deleteRecording = async <T>(recordingId: string, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(recordingByIdPath(recordingId), 'DELETE', {}, {}, null, parser);

    /** Services */

    static readonly getServices = async <T>(params?: ServiceQueryParams, parser?: (data: any) => any): Promise<FetchResponse<T>> =>
        apiExecute(servicesPath(), 'GET', {}, { ...params, skipPageParam: true }, null, parser, true);

    static readonly createServicePurchase = async <T>(
        productId: number,
        purchasePin: string,
        parser?: (data: any) => any
    ): Promise<FetchResponse<T>> => {
        const bodyObj = {
            product_id: productId,
            purchase_pin: purchasePin,
        };

        return apiExecute(servicePurchasePath(), 'POST', {}, {}, bodyObj, parser, true);
    };

    /** Search */

    static readonly search = async <T>(queryString: string, parser?: (data: any) => T): Promise<FetchResponse<T>> => {
        const params = new URLSearchParams({
            query_string: queryString,
        });

        return apiExecute(`${searchPath()}?${params.toString()}`, 'GET', null, {}, undefined, parser);
    };

    /** Customer details */

    static readonly getCustomerDetails = async (lang: string): Promise<FetchResponse<CustomerDetailsResponse>> =>
        apiExecute(customerDetailsPath(), 'GET', {}, { lang, skipPageParam: true }, null, null, true);
}
